import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Scissors, ArrowRight, ArrowLeft, KeyRound, Delete, UserPlus } from "lucide-react";

export default function EmployeeLoginPage() {
  const [mode, setMode] = useState("select"); // "select" | "pin" | "email" | "register"
  const [staff, setStaff] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    api.get("/employee-pins/staff").then(r => setStaff(r.data || [])).catch(() => {});
  }, []);

  const selectUser = (user) => {
    setSelectedUser(user);
    setPin("");
    setError("");
    setMode("pin");
  };

  const handlePinDigit = (digit) => {
    if (pin.length >= 4) return;
    const next = pin + digit;
    setPin(next);
    setError("");
    if (next.length === 4) submitPin(next);
  };

  const handlePinDelete = () => {
    setPin(p => p.slice(0, -1));
    setError("");
  };

  const submitPin = async (pinCode) => {
    setLoading(true);
    try {
      const res = await api.post("/employee-pins/login", { user_id: selectedUser.user_id, pin: pinCode });
      login(res.data.user, res.data.token);
      navigate("/employee", { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || "Incorrect PIN");
      setPin("");
    }
    setLoading(false);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const id = (email || "").trim();
      const payload = id.includes("@") ? { email: id } : { phone: id };
      const res = await api.post("/auth/login", { ...payload, password });
      login(res.data.user, res.data.token);
      navigate("/employee", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const id = (email || "").trim();
      const payload = id.includes("@") ? { email: id } : { phone: id };
      const res = await api.post("/auth/register", { ...payload, password, name, role: "stylist" });
      login(res.data.user, res.data.token);
      navigate("/employee", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  const goBack = () => { setMode("select"); setPin(""); setError(""); setSelectedUser(null); };

  // ─── Staff Grid ───
  if (mode === "select") {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="employee-login-page">
        <div className="flex-1 flex flex-col items-center justify-center p-6 max-w-xl mx-auto w-full">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center mx-auto shadow-lg shadow-rose-500/20 mb-4">
              <Scissors className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">Who's clocking in?</h1>
            <p className="text-sm text-slate-400 mt-1 font-['Manrope']">Tap your name to enter your PIN</p>
          </div>

          {staff.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 w-full mb-8" data-testid="staff-grid">
              {staff.map(s => (
                <button key={s.user_id} onClick={() => selectUser(s)} data-testid={`staff-select-${s.user_id}`}
                  className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col items-center gap-2 hover:shadow-lg hover:-translate-y-1 hover:border-rose-300 transition-all duration-200 active:scale-95 group">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-semibold shadow-sm transition-transform group-hover:scale-110"
                    style={{ backgroundColor: s.color || "#F43F5E" }}>
                    {s.name?.[0]}
                  </div>
                  <span className="text-sm font-semibold text-slate-800 font-['Manrope'] text-center">{s.name}</span>
                  <span className="text-[10px] text-slate-400 capitalize font-['Manrope']">{s.role?.replace("_", " ")}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 mb-8">
              <p className="text-sm text-slate-400 font-['Manrope']">No staff PINs configured yet</p>
              <p className="text-xs text-slate-300 mt-1 font-['Manrope']">Use email login below, or ask a manager to set up PINs</p>
            </div>
          )}

          <div className="w-full space-y-3">
            <button onClick={() => { setMode("email"); setError(""); }} data-testid="use-email-btn"
              className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border border-slate-200 text-sm font-medium text-slate-500 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-600 transition-all font-['Manrope']">
              Use email & password instead
            </button>
            <button onClick={() => { setMode("register"); setError(""); setName(""); setEmail(""); setPassword(""); }} data-testid="create-account-btn"
              className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border border-slate-200 text-sm font-medium text-slate-500 hover:bg-violet-50 hover:border-violet-200 hover:text-violet-600 transition-all font-['Manrope']">
              <UserPlus className="w-4 h-4" /> Create new account
            </button>
            <div className="text-center pt-2">
              <button onClick={() => navigate("/login")} className="text-xs text-slate-400 hover:text-rose-500 transition-colors font-['Manrope']" data-testid="emp-manager-link">
                Manager? Go to full dashboard →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── PIN Pad ───
  if (mode === "pin" && selectedUser) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center p-6" data-testid="employee-login-page">
        <div className="w-full max-w-xs space-y-6">
          <button onClick={goBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-rose-500 transition-colors font-['Manrope'] mb-2" data-testid="pin-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="text-center space-y-3">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-semibold mx-auto shadow-lg"
              style={{ backgroundColor: selectedUser.color || "#F43F5E" }}>
              {selectedUser.name?.[0]}
            </div>
            <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900">{selectedUser.name}</h2>
            <p className="text-sm text-slate-400 font-['Manrope']">Enter your 4-digit PIN</p>
          </div>

          {/* PIN Dots */}
          <div className="flex justify-center gap-4" data-testid="pin-dots">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className={`w-4 h-4 rounded-full transition-all duration-200 ${
                i < pin.length ? "bg-rose-500 scale-110" : "bg-slate-200"
              }`} />
            ))}
          </div>

          {error && <p className="text-sm text-rose-500 text-center font-semibold font-['Manrope']" data-testid="pin-error">{error}</p>}
          {loading && <p className="text-sm text-slate-400 text-center font-['Manrope']">Signing in...</p>}

          {/* Number Pad */}
          <div className="grid grid-cols-3 gap-3" data-testid="pin-pad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
              <button key={d} onClick={() => handlePinDigit(String(d))} data-testid={`pin-key-${d}`}
                className="h-16 rounded-2xl bg-white border border-slate-200 text-2xl font-semibold text-slate-800 hover:bg-rose-50 hover:border-rose-300 active:scale-95 transition-all duration-150 shadow-sm font-['Outfit']">
                {d}
              </button>
            ))}
            <div /> {/* empty */}
            <button onClick={() => handlePinDigit("0")} data-testid="pin-key-0"
              className="h-16 rounded-2xl bg-white border border-slate-200 text-2xl font-semibold text-slate-800 hover:bg-rose-50 hover:border-rose-300 active:scale-95 transition-all duration-150 shadow-sm font-['Outfit']">
              0
            </button>
            <button onClick={handlePinDelete} data-testid="pin-delete"
              className="h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-rose-50 hover:text-rose-500 active:scale-95 transition-all duration-150 shadow-sm">
              <Delete className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Email Login / Register ───
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6" data-testid="employee-login-page">
      <div className="w-full max-w-sm animate-fadeIn">
        <button onClick={goBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-rose-500 transition-colors font-['Manrope'] mb-6" data-testid="email-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to quick login
        </button>

        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center mx-auto shadow-lg shadow-rose-500/20 mb-4">
            <Scissors className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">
            {mode === "register" ? "Create Employee Account" : "Employee Login"}
          </h1>
          <p className="text-sm text-slate-400 mt-1 font-['Manrope']">
            {mode === "register" ? "Set up your account to get started" : "Sign in with email & password"}
          </p>
        </div>

        <form onSubmit={mode === "register" ? handleRegister : handleEmailSubmit} className="space-y-4" data-testid="employee-login-form">
          {mode === "register" && (
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1.5 block font-['Manrope']">Full Name</label>
              <Input data-testid="emp-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name"
                className="h-12 bg-white border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 rounded-xl text-slate-900" required />
            </div>
          )}
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-1.5 block font-['Manrope']">Email or phone number</label>
            <Input data-testid="emp-email" type="text" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username"
              value={email} onChange={e => setEmail(e.target.value)} placeholder="you@salon.com or (555) 555-5555"
              className="h-12 bg-white border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 rounded-xl text-slate-900" required />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-1.5 block font-['Manrope']">Password</label>
            <Input data-testid="emp-password" type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder={mode === "register" ? "Create a password" : "Enter your password"}
              className="h-12 bg-white border-slate-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 rounded-xl text-slate-900" required />
          </div>

          {error && <div data-testid="emp-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 font-['Manrope']">{error}</div>}

          <Button data-testid="emp-submit" type="submit" disabled={loading}
            className="w-full h-12 gradient-primary text-white rounded-xl text-base font-semibold shadow-[0_4px_14px_rgba(244,63,94,0.35)] hover:-translate-y-0.5 transition-all duration-200">
            {loading ? "Please wait..." : mode === "register" ? "Create Account" : "Sign In"}
            {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-slate-500 font-['Manrope']">
          {mode === "register" ? "Already have an account?" : "Don't have an account?"}{" "}
          <button data-testid="emp-toggle-auth" onClick={() => { setMode(mode === "register" ? "email" : "register"); setError(""); }}
            className="text-rose-600 font-semibold hover:text-rose-700 transition-colors">
            {mode === "register" ? "Sign in" : "Create one"}
          </button>
        </p>
      </div>
    </div>
  );
}
